"use client"

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { programmaticFocus } from "@/utils/programmaticFocus"

const FOCUSABLE_SELECTOR = [
  "[data-popup-focus]",
  'a[href]:not([disabled]):not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
].join(", ")

interface UseFocusTrapOptions {
  isOpen: boolean
  onClose?: () => void
}

// 모듈 레벨 포커스 복원 스택
// 모달이 열릴 때 push, 닫힐 때 자신의 요소가 DOM에 없으면 스택을 역순 탐색하여 복원
const focusReturnStack: HTMLElement[] = []

// 모듈 레벨: 열려 있는 모달 개수 카운터
// VoiceOver/TalkBack이 모달 외부로 탈출하는 것을 방지하기 위해
// 모달이 하나라도 열려 있으면 배경(app-root)에 inert + aria-hidden 적용
let activeModalCount = 0

function updateBackgroundInert() {
  const appRoot = document.getElementById("app-root")
  if (!appRoot) return

  if (activeModalCount > 0) {
    appRoot.setAttribute("inert", "")
    appRoot.setAttribute("aria-hidden", "true")
  } else {
    appRoot.removeAttribute("inert")
    appRoot.removeAttribute("aria-hidden")
  }
}

export const useFocusTrap = ({ isOpen, onClose }: UseFocusTrapOptions) => {
  const containerRef = useRef<HTMLDivElement>(null) // 컨테이너 DOM 참조 (querySelectorAll 대상)
  const previousActiveElement = useRef<HTMLElement | null>(null) // 열기 전 포커스 위치 저장
  const [animationComplete, setAnimationComplete] = useState(false) // 애니메이션 완료 여부 (포커스 이동 트리거)
  const isOpenRef = useRef(isOpen) // 애니메이션 완료 감지용 (enter vs exit 구분)
  const isContributingRef = useRef(false) // 이 인스턴스가 activeModalCount에 기여 중인지

  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return []
    return Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => {
      // hidden 또는 display:none 체크
      const style = window.getComputedStyle(el)
      return style.display !== "none" && style.visibility !== "hidden"
    })
  }, [])

  // isOpenRef 동기화 (handleAnimationComplete에서 enter/exit 구분용)
  useEffect(() => {
    isOpenRef.current = isOpen
  }, [isOpen])

  // 열릴 때: 이전 포커스 저장 (useLayoutEffect → useEffect보다 먼저 실행)
  // inert가 app-root에 적용되면 브라우저가 내부 요소를 blur하여
  // document.activeElement가 document.body로 바뀌므로, inert 설정 전에 저장해야 함
  useLayoutEffect(() => {
    if (isOpen) {
      const activeEl = document.activeElement as HTMLElement
      previousActiveElement.current = activeEl
      // DOM에서 사라진 stale 항목 정리 후 push
      for (let i = focusReturnStack.length - 1; i >= 0; i--) {
        if (!document.body.contains(focusReturnStack[i])) {
          focusReturnStack.splice(i, 1)
        }
      }
      focusReturnStack.push(activeEl)
      setAnimationComplete(false)
    }
  }, [isOpen])

  // 배경 inert 관리 (VoiceOver/TalkBack 모달 외부 탈출 방지)
  // useEffect이므로 위의 useLayoutEffect 이후에 실행됨
  useEffect(() => {
    if (isOpen && !isContributingRef.current) {
      isContributingRef.current = true
      activeModalCount++
      updateBackgroundInert()
    } else if (!isOpen && isContributingRef.current) {
      isContributingRef.current = false
      activeModalCount--
      updateBackgroundInert()
    }
  }, [isOpen])

  // unmount 시 카운터 정리
  useEffect(() => {
    return () => {
      if (isContributingRef.current) {
        isContributingRef.current = false
        activeModalCount--
        updateBackgroundInert()
      }
    }
  }, [])

  // 애니메이션 완료 후 포커스 이동
  useEffect(() => {
    if (isOpen && animationComplete) {
      const focusableElements = getFocusableElements()
      if (focusableElements.length > 0) {
        programmaticFocus(focusableElements[0])
      } else {
        programmaticFocus(containerRef.current)
      }
    }
  }, [isOpen, animationComplete, getFocusableElements])

  // framer-motion onAnimationComplete 핸들러
  // isOpenRef로 enter/exit 구분 (애니메이션 타입에 의존하지 않음)
  const handleAnimationComplete = useCallback(() => {
    if (isOpenRef.current) {
      setAnimationComplete(true)
    }
  }, [])

  // 닫힐 때: 이전 포커스 복원
  // requestAnimationFrame으로 inert 제거가 브라우저에 반영된 후 포커스 복원
  useEffect(() => {
    if (!isOpen && previousActiveElement.current) {
      const elementToRestore = previousActiveElement.current
      previousActiveElement.current = null
      requestAnimationFrame(() => {
        if (document.body.contains(elementToRestore)) {
          programmaticFocus(elementToRestore)
        } else {
          // DOM에 없으면 스택을 역순 탐색하여 유효한 요소로 복원
          // (예: 바텀시트가 닫히면서 메뉴 버튼이 사라진 경우 → 메인 버튼으로 복원)
          while (focusReturnStack.length > 0) {
            const el = focusReturnStack.pop()!
            if (document.body.contains(el)) {
              programmaticFocus(el)
              break
            }
          }
        }
      })
    }
  }, [isOpen])

  // 키보드 이벤트 핸들링 (포커스 트랩 + ESC)
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // 포커스가 이 컨테이너 밖에 있으면 무시 (중첩 모달 지원)
      if (!containerRef.current?.contains(document.activeElement)) return

      // ESC 키: 닫기
      if (event.key === "Escape") {
        event.preventDefault()
        onClose?.()
        return
      }

      // Tab 키: 포커스 트랩
      if (event.key === "Tab") {
        const focusableElements = getFocusableElements()
        if (focusableElements.length === 0) {
          event.preventDefault()
          return
        }

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        // Shift + Tab: 첫 번째 요소에서 마지막으로 이동
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
          return
        }

        // Tab: 마지막 요소에서 첫 번째로 이동
        if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
          return
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, onClose, getFocusableElements])

  // isOpen 유지된 상태에서 content가 교체될 때 호출 (예: 팝업→팝업, 글로벌팝업 큐)
  // DOM 업데이트 후 첫 번째 포커스 가능 요소로 이동
  const refocus = useCallback(() => {
    requestAnimationFrame(() => {
      const focusableElements = getFocusableElements()
      if (focusableElements.length > 0) {
        programmaticFocus(focusableElements[0])
      } else {
        programmaticFocus(containerRef.current)
      }
    })
  }, [getFocusableElements])

  return { containerRef, handleAnimationComplete, refocus }
}
